import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { AggregatedUserPick } from '../types';

interface RawAggregatedRow {
  place_id: string;
  user_pick_count: number;
  user_avg_rank: number | string;
  sample_name: string;
  sample_cuisine: string | null;
  sample_location: string | null;
}

function isRawAggregatedRow(value: unknown): value is RawAggregatedRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.place_id === 'string' &&
    typeof v.user_pick_count === 'number' &&
    (typeof v.user_avg_rank === 'number' || typeof v.user_avg_rank === 'string') &&
    typeof v.sample_name === 'string'
  );
}

function rawToAggregated(row: RawAggregatedRow): AggregatedUserPick {
  return {
    placeId: row.place_id,
    userPickCount: row.user_pick_count,
    userAvgRank: typeof row.user_avg_rank === 'string'
      ? parseFloat(row.user_avg_rank)
      : row.user_avg_rank,
    sampleName: row.sample_name,
    sampleCuisine: row.sample_cuisine,
    sampleLocation: row.sample_location,
  };
}

interface UseAggregatedPicksResult {
  picks: AggregatedUserPick[];
  isLoaded: boolean;
  error: string | null;
}

export function useAggregatedPicks(): UseAggregatedPicksResult {
  const [picks, setPicks] = useState<AggregatedUserPick[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const { data, error: fetchError } = await supabase
        .from('aggregated_picks')
        .select('place_id, user_pick_count, user_avg_rank, sample_name, sample_cuisine, sample_location');
      if (!mounted) return;
      if (fetchError) {
        setError(fetchError.message);
        setIsLoaded(true);
        return;
      }
      const safe = Array.isArray(data)
        ? data.filter(isRawAggregatedRow).map(rawToAggregated)
        : [];
      setPicks(safe);
      setIsLoaded(true);
      setError(null);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return { picks, isLoaded, error };
}
